import React, { useEffect, useState } from 'react';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import { getImageQualityReport } from './api';
import { CohortFilter, ListCohort } from './constants';
import { GetImageQualityReportOutput, ReportWindow } from './model';
import {
  filterByCohort,
  formatPct,
  largestDriver,
  passRatePct,
  qualityTier,
} from './imageQualityUtils';
import { useReportFetch, reportRequestKey } from './useReportFetch';
import FailureBreakdown from './FailureBreakdown';
import AssociateList from './AssociateList';
import {
  AccentCard,
  CohortChips,
  Label,
  ListCohortPill,
  LoadFailed,
  PageTitle,
  PrintButton,
  StatCard,
  textColorForTier,
} from './meridianUi';

/** The Boson-selected site's rollup: header + rank, tiles, where to focus, breakdown, then associates by cohort. */
export interface SiteViewProps {
  /** Told the earliest operating day with data, so the page calendars can start there. */
  onDataAvailableFrom?: (day?: string) => void;
  warehouseId: string;
  reportWindow: ReportWindow;
  windowLabel: string;
  /** Date range controls, shown under the headline tiles. */
  windowBar: React.ReactNode;
  onSelectAssociate: (login: string) => void;
}

/** Associates is the only tab, so picking it changes nothing. */
const keepAssociatesTab = () => undefined;

const SiteView: React.FC<SiteViewProps> = ({
  onDataAvailableFrom,
  warehouseId,
  reportWindow,
  windowLabel,
  windowBar,
  onSelectAssociate,
}) => {
  const {
    data: report,
    loading,
    failed,
    retry,
  } = useReportFetch(
    () => getImageQualityReport({ warehouseId, ...reportWindow }),
    reportRequestKey(reportWindow, warehouseId)
  );

  useEffect(() => {
    onDataAvailableFrom?.(report?.dataAvailableFrom);
  }, [report, onDataAvailableFrom]);

  const rank = report?.summary.networkRank;

  return (
    <Column spacing="500" data-testid="iq-site-view">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <PageTitle
          title={warehouseId}
          sub={`Return center · ${windowLabel}`}
          capturedAt={report?.latestCapturedAt}
        />
        {rank !== undefined && (
          <Column spacing="100" alignmentHorizontal="end" data-testid="iq-network-rank">
            <Label>National rank</Label>
            <Row spacing="100" alignmentVertical="baseline">
              <Text type="h600">{rank}</Text>
              {report?.summary.networkSiteCount !== undefined && (
                <Text type="b500" color="secondary">{`of ${report.summary.networkSiteCount}`}</Text>
              )}
            </Row>
          </Column>
        )}
      </Row>

      {loading && (
        <>
          {windowBar}
          <Row alignmentHorizontal="center" spacingInset="500">
            <Loader size="large" />
          </Row>
        </>
      )}

      {failed && (
        <>
          {windowBar}
          <LoadFailed
            message="Couldn't load the report."
            onRetry={retry}
            testId="iq-report-failed"
          />
        </>
      )}

      {!loading && report && report.summary.totalPhotos === 0 && (
        <>
          {windowBar}
          <Card spacingInset="500" data-testid="iq-empty">
            <Text color="secondary">No evidence photos were captured in this range.</Text>
          </Card>
        </>
      )}

      {!loading && report && report.summary.totalPhotos > 0 && (
        <SiteReport report={report} windowBar={windowBar} onSelectAssociate={onSelectAssociate} />
      )}
    </Column>
  );
};

const SiteReport: React.FC<{
  report: GetImageQualityReportOutput;
  windowBar: React.ReactNode;
  onSelectAssociate: (login: string) => void;
}> = ({ report, windowBar, onSelectAssociate }) => {
  const [cohort, setCohort] = useState<CohortFilter>('');
  const { summary, thresholds } = report;
  const driver = largestDriver(report.failureBreakdown);
  const pct = passRatePct(summary.passingPhotos, summary.totalPhotos);
  const tier = qualityTier(pct, thresholds);
  // Until the backend sends every associate, the cohorts are the two lists it does send, its best
  // few and its bottom share, and the threshold cohorts are disabled. At a small site the lists can
  // overlap, so All lists each associate once.
  const { highestAssociates, lowestAssociates } = report;
  const listCohorts: ListCohortPill[] | undefined = report.associates
    ? undefined
    : [
        {
          value: ListCohort.BEST_FEW,
          label: `Top ${highestAssociates.length}`,
          count: highestAssociates.length,
          tier: 'good',
        },
        {
          value: ListCohort.BOTTOM_SHARE,
          label: `Bottom ${thresholds.lowestPercent}%`,
          count: lowestAssociates.length,
          tier: 'bad',
        },
      ];
  const bottomShareLogins = new Set(lowestAssociates.map(associate => associate.login));
  const everyoneListed = report.associates ?? [
    ...lowestAssociates,
    ...highestAssociates.filter(associate => !bottomShareLogins.has(associate.login)),
  ];
  const associatesInCohort =
    cohort === ListCohort.BEST_FEW
      ? highestAssociates
      : cohort === ListCohort.BOTTOM_SHARE
        ? lowestAssociates
        : filterByCohort(everyoneListed, thresholds, cohort);

  return (
    <>
      <Row spacing="400" widths="fill" alignmentVertical="stretch" data-testid="iq-site-cards">
        <StatCard
          label="Image quality"
          hero={formatPct(pct)}
          heroColor={textColorForTier(tier)}
          sub={`${summary.passingPhotos.toLocaleString()} of ${summary.totalPhotos.toLocaleString()} pass`}
        />
        <StatCard label="Photos" hero={summary.totalPhotos.toLocaleString()} sub="in this range" />
        <StatCard
          label="Associates"
          hero={summary.associateCount.toLocaleString()}
          sub="active in this range"
        />
        <StatCard
          label={`Below ${thresholds.floorPassRatePct}% repeatedly`}
          hero={summary.repeatBelowFloorCount.toLocaleString()}
          heroColor={summary.repeatBelowFloorCount > 0 ? 'error' : 'success'}
          sub={`in ${thresholds.repeatMinWeeks}+ of the last ${thresholds.repeatWindowWeeks} weeks`}
        />
      </Row>

      {windowBar}

      <AccentCard
        testId="iq-focus-summary"
        aside={
          <Column spacing="400">
            <Label>Failure breakdown</Label>
            <FailureBreakdown breakdown={report.failureBreakdown} />
          </Column>
        }
      >
        <Text type="b500">
          {driver ? (
            <>
              <strong>{driver.failureLabel}</strong> is the largest driver at this site —{' '}
              <strong>{driver.failedPhotos.toLocaleString()}</strong> photos,{' '}
              {formatPct(driver.sharePct)} of all failures in this range.{' '}
            </>
          ) : (
            'No failed checks in this range. '
          )}
          <strong>{summary.repeatBelowFloorCount}</strong> associates were below{' '}
          {thresholds.floorPassRatePct}% in {thresholds.repeatMinWeeks} or more of the last{' '}
          {thresholds.repeatWindowWeeks} weeks.
        </Text>
      </AccentCard>

      <Card spacingInset="none">
        {/* The site's one breakdown today; more tabs can join it beside Associates. */}
        <Row spacingInset="300 500">
          <TabGroup value="associates" onChange={keepAssociatesTab} data-testid="iq-site-tabs">
            <Tab value="associates" data-testid="iq-tab-associates">
              Associates
            </Tab>
          </TabGroup>
        </Row>
        <Column spacing="300">
          <Row
            alignmentHorizontal="justify"
            alignmentVertical="center"
            spacingInset="300 500"
            wrap="down"
          >
            <CohortChips
              associates={everyoneListed}
              thresholds={thresholds}
              listCohorts={listCohorts}
              value={cohort}
              onChange={setCohort}
            />
            <PrintButton />
          </Row>
          <AssociateList
            associates={associatesInCohort}
            thresholds={thresholds}
            onSelect={onSelectAssociate}
            emptyText={
              report.associates
                ? 'No associates in this cohort.'
                : 'No associates with enough photos to rank in this range.'
            }
          />
        </Column>
      </Card>
    </>
  );
};

export default SiteView;
